import { ComponentProps, FC } from "react";

/**
 * Custom Sparkles icon, drawn to sit visually with the Heroicons family
 * (same grids, round caps/joins, 0.75 tip radius on solids) but lighter than
 * the stock one: two sparkles instead of three, and 22°/24° tips so the two
 * stroke edges of each arm stay apart instead of merging into a solid wedge.
 *
 * The outline is stroked at 1.25 rather than Heroicons' 1.5. That is optical
 * compensation, not a mismatch: a closed star has ~2× the ink density of the
 * open strokes it sits next to (plus, chevron, ellipsis), and at the sizes this
 * icon renders in the app (10–20px) the two edges of each arm are ≤1 device px
 * apart, so a 1.5 stroke fuses them into a bar twice the weight of a plus.
 * At 1.25 the measured weight matches its siblings from 12 to 20px, while the
 * 0.25-unit nominal difference is ≤0.2 CSS px and not visible as such. Solids
 * are silhouettes with Heroicons' 0.75 tip radius, so they need no adjustment.
 *
 * `variant` mirrors the Heroicons entry points it replaces:
 * - `outline` → `@heroicons/react/24/outline` (24×24, stroked)
 * - `solid`   → `@heroicons/react/24/solid`   (24×24, filled)
 * - `mini`    → `@heroicons/react/20/solid`   (20×20, filled)
 * - `micro`   → `@heroicons/react/16/solid`   (16×16, filled)
 */
type SparklesIconProps = {
  variant?: "outline" | "solid" | "mini" | "micro";
} & ComponentProps<"svg">;

const OUTLINE_STROKE = 1.25;

const OUTLINE_24 =
  "M9.375 7.5L10.861 11.177A3.551 3.551 0 0 0 12.823 13.139L16.5 14.625L12.823 16.111A3.551 3.551 0 0 0 10.861 18.073L9.375 21.75L7.889 18.073A3.551 3.551 0 0 0 5.927 16.111L2.25 14.625L5.927 13.139A3.551 3.551 0 0 0 7.889 11.177Z" +
  "M18 2.25L18.461 3.286A4.445 4.445 0 0 0 20.714 5.539L21.75 6L20.714 6.461A4.445 4.445 0 0 0 18.461 8.714L18 9.75L17.539 8.714A4.445 4.445 0 0 0 15.286 6.461L14.25 6L15.286 5.539A4.445 4.445 0 0 0 17.539 3.286Z";

// Outer silhouette of OUTLINE_24 at a 0.75 offset (Heroicons' solid tip radius).
const SOLID_24 =
  "M8.68 7.219A.75 .75 0 0 1 10.07 7.219L11.556 10.896A2.801 2.801 0 0 0 13.104 12.444L16.781 13.93A.75 .75 0 0 1 16.781 15.32L13.104 16.806A2.801 2.801 0 0 0 11.556 18.354L10.07 22.031A.75 .75 0 0 1 8.68 22.031L7.194 18.354A2.801 2.801 0 0 0 5.646 16.806L1.969 15.32A.75 .75 0 0 1 1.969 13.93L5.646 12.444A2.801 2.801 0 0 0 7.194 10.896Z" +
  "M17.315 1.945A.75 .75 0 0 1 18.685 1.945L19.146 2.981A3.695 3.695 0 0 0 21.019 4.854L22.055 5.315A.75 .75 0 0 1 22.055 6.685L21.019 7.146A3.695 3.695 0 0 0 19.146 9.019L18.685 10.055A.75 .75 0 0 1 17.315 10.055L16.854 9.019A3.695 3.695 0 0 0 14.981 7.146L13.945 6.685A.75 .75 0 0 1 13.945 5.315L14.981 4.854A3.695 3.695 0 0 0 16.854 2.981Z";

const SOLID_20 =
  "M7.305 5.969A.75 .75 0 0 1 8.695 5.969L10.055 9.335A1.103 1.103 0 0 0 10.665 9.945L14.031 11.305A.75 .75 0 0 1 14.031 12.695L10.665 14.055A1.103 1.103 0 0 0 10.055 14.665L8.695 18.031A.75 .75 0 0 1 7.305 18.031L5.945 14.665A1.103 1.103 0 0 0 5.335 14.055L1.969 12.695A.75 .75 0 0 1 1.969 11.305L5.335 9.945A1.103 1.103 0 0 0 5.945 9.335Z" +
  "M14.065 1.945A.75 .75 0 0 1 15.435 1.945L15.936 3.07A1.962 1.962 0 0 0 16.93 4.064L18.055 4.565A.75 .75 0 0 1 18.055 5.935L16.93 6.436A1.962 1.962 0 0 0 15.936 7.43L15.435 8.555A.75 .75 0 0 1 14.065 8.555L13.564 7.43A1.962 1.962 0 0 0 12.57 6.436L11.445 5.935A.75 .75 0 0 1 11.445 4.565L12.57 4.064A1.962 1.962 0 0 0 13.564 3.07Z";

const SOLID_16 =
  "M5.555 4.969A.75 .75 0 0 1 6.945 4.969L7.967 7.496A.972 .972 0 0 0 8.504 8.033L11.031 9.055A.75 .75 0 0 1 11.031 10.445L8.504 11.467A.972 .972 0 0 0 7.967 12.004L6.945 14.531A.75 .75 0 0 1 5.555 14.531L4.533 12.004A.972 .972 0 0 0 3.996 11.467L1.469 10.445A.75 .75 0 0 1 1.469 9.055L3.996 8.033A.972 .972 0 0 0 4.533 7.496Z" +
  "M11.065 1.445A.75 .75 0 0 1 12.435 1.445L12.819 2.308A1.721 1.721 0 0 0 13.692 3.181L14.555 3.565A.75 .75 0 0 1 14.555 4.935L13.692 5.319A1.721 1.721 0 0 0 12.819 6.192L12.435 7.055A.75 .75 0 0 1 11.065 7.055L10.681 6.192A1.721 1.721 0 0 0 9.808 5.319L8.945 4.935A.75 .75 0 0 1 8.945 3.565L9.808 3.181A1.721 1.721 0 0 0 10.681 2.308Z";

const SOLID = {
  solid: { size: 24, d: SOLID_24 },
  mini: { size: 20, d: SOLID_20 },
  micro: { size: 16, d: SOLID_16 },
} as const;

const SparklesIcon: FC<SparklesIconProps> = ({
  variant = "outline",
  ...rest
}) => {
  if (variant === "outline") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={OUTLINE_STROKE}
        stroke="currentColor"
        aria-hidden="true"
        data-slot="icon"
        {...rest}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={OUTLINE_24} />
      </svg>
    );
  }

  const { size, d } = SOLID[variant];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${size} ${size}`}
      fill="currentColor"
      aria-hidden="true"
      data-slot="icon"
      {...rest}
    >
      <path fillRule="evenodd" clipRule="evenodd" d={d} />
    </svg>
  );
};

export default SparklesIcon;
